// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Types for analytics events
export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

/**
 * Initialize analytics utilities
 * GTM is already loaded via HTML, this just sets up additional configuration
 */
export const initializeAnalytics = () => {
  const isDevelopment = import.meta.env.DEV;

  try {
    // Ensure dataLayer exists (it should already from HTML GTM)
    window.dataLayer = window.dataLayer || [];

    // Define gtag function if not already defined
    window.gtag ??= function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };

    console.log(`[Analytics] GTM utilities initialized (GTM loaded via HTML)`);

    // Set development flag for debugging
    if (isDevelopment) {
      window.gtag('set', { debug_mode: true });
      console.log('[Analytics] Debug mode enabled for development');
    }

    // Set custom user properties mapping
    window.gtag('set', {
      custom_map: {
        custom_dimension_1: 'shelter_name',
        custom_dimension_2: 'user_type',
      },
    });

    return true;
  } catch (error) {
    console.error('[Analytics] Failed to initialize analytics utilities:', error);
    return false;
  }
};

/**
 * Track page views
 */
export const trackPageView = (path: string, title?: string) => {
  if (!window.gtag) {
    console.warn('[Analytics] GTM not initialized, skipping page view tracking');
    return;
  }

  try {
    window.gtag('event', 'page_view', {
      page_title: title ?? document.title,
      page_location: window.location.href,
      page_path: path,
    });
    console.log(`[Analytics] Page view tracked: ${path}`);
  } catch (error) {
    console.error('[Analytics] Failed to track page view:', error);
  }
};

/**
 * Track custom events
 */
export const trackEvent = (event: AnalyticsEvent) => {
  if (!window.gtag) {
    console.warn('[Analytics] GTM not initialized, skipping event tracking');
    return;
  }

  try {
    window.gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
    });
    console.log('[Analytics] Event tracked:', event);
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
};

/**
 * Set user properties
 */
export const setUserProperties = (properties: Record<string, string | number>) => {
  if (!window.gtag) {
    console.warn('[Analytics] GTM not initialized, skipping user properties');
    return;
  }

  try {
    window.gtag('set', properties);
    console.log('[Analytics] User properties set:', properties);
  } catch (error) {
    console.error('[Analytics] Failed to set user properties:', error);
  }
};

/**
 * Push raw event directly to dataLayer
 * Use this when you need exact dataLayer format without gtag transformation
 */
export const pushToDataLayer = (eventData: Record<string, string | number | boolean | null>) => {
  if (!window.dataLayer) {
    console.warn('[Analytics] DataLayer not initialized, skipping event push');
    return;
  }

  try {
    window.dataLayer.push(eventData);
    console.log('[Analytics] Event pushed to dataLayer:', eventData);
  } catch (error) {
    console.error('[Analytics] Failed to push to dataLayer:', error);
  }
};

// Helper function to convert seconds to time string (HH:MM:SS or MM:SS)
const secondsToTimeString = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export interface WelcomeFlowTracking {
  quizName: string;
  questionNames: string[];
}

// Common event tracking functions for your app
export const analytics = {
  // Navigation events - Direct dataLayer push for exact format
  navigate: (
    destination: string,
    clickText: string,
    navGroup: string = 'mainnav',
    category: string | null = null
  ) => {
    // Push exact format directly to dataLayer
    pushToDataLayer({
      event: 'navigation',
      nav_group: navGroup,
      category: category,
      click_url: destination,
      click_text: clickText,
    });
  },

  // Report events
  generateReport: (reportType: string) =>
    trackEvent({
      action: 'generate_report',
      category: 'reports',
      label: reportType,
    }),

  downloadReport: (reportType: string) =>
    trackEvent({
      action: 'download_report',
      category: 'reports',
      label: reportType,
    }),

  // Resource events
  downloadResource: (resourceType: string) =>
    trackEvent({
      action: 'download_resource',
      category: 'resources',
      label: resourceType,
    }),

  // Authentication events
  loginSuccess: () =>
    trackEvent({
      action: 'login_success',
      category: 'authentication',
      label: 'gigya',
    }),

  loginFailure: (error: string) =>
    trackEvent({
      action: 'login_failure',
      category: 'authentication',
      label: error,
    }),

  // Error tracking
  trackError: (errorMessage: string, errorType: string) =>
    trackEvent({
      action: 'error',
      category: 'errors',
      label: `${errorType}: ${errorMessage}`,
    }),

  // Button and CTA click tracking
  buttonClick: (buttonName: string, location?: string) =>
    trackEvent({
      action: 'click',
      category: 'button',
      label: location ? `${buttonName}_${location}` : buttonName,
    }),

  ctaClick: (action: string, category: string, details?: string) => {
    pushToDataLayer({
      event: 'cta',
      action,
      category,
      details: details ?? '',
    });
  },

  // CTA tracking with click_text and click_url
  ctaClickWithUrl: (clickText: string, clickUrl: string) => {
    pushToDataLayer({
      event: 'cta',
      click_text: clickText,
      click_url: clickUrl,
    });
  },

  linkClick: (linkText: string, linkUrl: string, isExternal: boolean = false) =>
    trackEvent({
      action: 'click',
      category: isExternal ? 'external_link' : 'internal_link',
      label: `${linkText}_${linkUrl}`,
    }),

  // Specific UI interactions
  modalOpen: (modalName: string) =>
    trackEvent({
      action: 'open',
      category: 'modal',
      label: modalName,
    }),

  modalClose: (modalName: string) =>
    trackEvent({
      action: 'close',
      category: 'modal',
      label: modalName,
    }),

  dropdownSelect: (dropdownName: string, selectedValue: string) =>
    trackEvent({
      action: 'select',
      category: 'dropdown',
      label: `${dropdownName}_${selectedValue}`,
    }),

  formSubmit: (formName: string, success: boolean = true) =>
    trackEvent({
      action: success ? 'submit_success' : 'submit_error',
      category: 'form',
      label: formName,
    }),

  // File and resource interactions
  fileDownload: (fileName: string, fileType: string) =>
    trackEvent({
      action: 'download',
      category: 'file',
      label: `${fileName}.${fileType}`,
    }),

  resourceView: (resourceName: string, resourceType: string) =>
    trackEvent({
      action: 'view',
      category: 'resource',
      label: `${resourceType}_${resourceName}`,
    }),

  // Search and filter actions
  searchPerformed: (searchTerm: string, resultsCount: number) =>
    trackEvent({
      action: 'search',
      category: 'search',
      label: searchTerm,
      value: resultsCount,
    }),

  filterApplied: (filterType: string, filterValue: string) =>
    trackEvent({
      action: 'filter',
      category: 'filter',
      label: `${filterType}_${filterValue}`,
    }),

  // Log out
  logout: () => {
    pushToDataLayer({
      event: 'login',
      action: 'logout',
      user_type: 'shelter_user',
      method: 'Gigya',
    });
  },

  videoStart: (
    videoTitle: string,
    videoDurationSeconds: number,
    videoUrl: string,
    videoProvider: string,
    visible: string = 'true'
  ) => {
    const duration = secondsToTimeString(videoDurationSeconds);
    pushToDataLayer({
      event: 'video_start',
      video_title: videoTitle,
      video_duration: duration,
      video_url: videoUrl,
      video_provider: videoProvider,
      video_percent: '0',
      video_current_time: '0:00',
      visible: visible,
    });
  },

  videoProgress: (
    videoTitle: string,
    videoDurationSeconds: number,
    videoProvider: string,
    videoCurrentTimeSeconds: number
  ) => {
    // Calculate video percent and format times
    const videoPercent =
      videoDurationSeconds > 0
        ? Math.round((videoCurrentTimeSeconds / videoDurationSeconds) * 100) + '%'
        : '0%';
    const duration = secondsToTimeString(videoDurationSeconds);
    const currentTime = secondsToTimeString(videoCurrentTimeSeconds);

    pushToDataLayer({
      event: 'video_progress',
      video_current_time: currentTime,
      video_percent: videoPercent,
      video_title: videoTitle,
      video_provider: videoProvider,
      video_duration: duration,
    });
  },

  videoComplete: (
    videoTitle: string,
    videoDurationSeconds: number,
    videoUrl: string,
    videoProvider: string,
    visible: string = 'true'
  ) => {
    const duration = secondsToTimeString(videoDurationSeconds);
    pushToDataLayer({
      event: 'video_complete',
      video_title: videoTitle,
      video_duration: duration,
      video_url: videoUrl,
      video_provider: videoProvider,
      video_percent: '100%',
      video_current_time: duration,
      visible: visible,
    });
  },

  // Welcome flow quiz tracking
  quizStart: (quizName: string) => {
    pushToDataLayer({
      event: 'quiz',
      action: 'start',
      quiz_name: quizName,
    });
  },

  quizAnswer: (quizName: string, question: string, answer: string) => {
    pushToDataLayer({
      event: 'quiz',
      action: 'answer',
      quiz_name: quizName,
      question,
      answer,
    });
  },

  quizEnd: (quizName: string) => {
    pushToDataLayer({
      event: 'quiz',
      action: 'end',
      quiz_name: quizName,
    });
  },

  // Custom error tracking
  customError: (category: string, errorMessage: string, details?: string) => {
    pushToDataLayer({
      event: 'custom_error',
      category: category,
      error_message: errorMessage,
      details: details ?? '',
    });
  },
};
